export type FooterState = { atBottom: boolean; visible: boolean };

export function attachFooterIntent(root: HTMLElement, onChange: (state: FooterState) => void) {
  const doc = root.ownerDocument;
  const view = doc.defaultView!;
  let state: FooterState = { atBottom: false, visible: false };
  let lastScroll = view.scrollY;
  let touchY: number | null = null;
  const atBottom = () => {
    const page = doc.scrollingElement || doc.documentElement;
    return page.scrollHeight - view.innerHeight - view.scrollY <= 2
      && root.getBoundingClientRect().bottom <= view.innerHeight + 2;
  };
  const publish = (visible: boolean) => {
    const bottom = atBottom();
    const next = { atBottom: bottom, visible: visible && bottom };
    if (next.visible !== state.visible || next.atBottom !== state.atBottom) {
      state = next;
      onChange(next);
    }
  };
  const close = () => publish(false);
  const open = () => publish(true);
  const accepts = (target: EventTarget | null) => {
    if (!(target instanceof view.Element)) return target === doc;
    return (root.contains(target) || target === doc.body || target === doc.documentElement)
      && !target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [aria-modal="true"]');
  };
  const canScrollInside = (target: EventTarget | null) => {
    let node = target instanceof view.Element ? target : null;
    while (node && node !== root && node !== doc.body) {
      if (/auto|scroll/.test(view.getComputedStyle(node).overflowY)
        && node.scrollHeight - node.clientHeight - node.scrollTop > 2) return true;
      node = node.parentElement;
    }
    return false;
  };
  const intent = (delta: number, target: EventTarget | null) => {
    if (!accepts(target)) return;
    if (delta < 0) close();
    else if (delta > 0 && !canScrollInside(target)) open();
  };
  const onWheel = (event: WheelEvent) => {
    if (!event.ctrlKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) intent(event.deltaY, event.target);
  };
  const onTouchStart = (event: TouchEvent) => { touchY = event.touches.length === 1 ? event.touches[0].clientY : null; };
  const onTouchMove = (event: TouchEvent) => {
    const y = event.touches.length === 1 ? event.touches[0].clientY : null;
    if (y !== null && touchY !== null) intent(touchY - y, event.target);
    touchY = y;
  };
  const onTouchEnd = () => { touchY = null; };
  const onKey = (event: KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !accepts(event.target)) return;
    if (['ArrowUp', 'PageUp', 'Home', 'Escape'].includes(event.key)) close();
    else if (!event.defaultPrevented && ['ArrowDown', 'PageDown', 'End'].includes(event.key)) intent(1, event.target);
  };
  const onScroll = () => {
    publish((state.visible && view.scrollY >= lastScroll) || (!state.atBottom && view.scrollY > lastScroll));
    lastScroll = view.scrollY;
  };
  const onResize = () => publish(state.visible);
  const events = { wheel: onWheel, touchstart: onTouchStart, touchmove: onTouchMove, touchend: onTouchEnd,
    touchcancel: onTouchEnd, keydown: onKey, scroll: onScroll, resize: onResize };
  // Слушаем намерение на границе страницы, но не перехватываем обычную прокрутку глав.
  for (const [type, listener] of Object.entries(events)) view.addEventListener(type, listener as EventListener, { passive: true });
  // При End/перетаскивании scrollbar футер монтируется уже после события scroll.
  publish(atBottom());
  return { open, close, refresh: onResize, dispose() {
    for (const [type, listener] of Object.entries(events)) view.removeEventListener(type, listener as EventListener);
  } };
}
